import {
  Context,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  CountryCode,
  COUNTRY_CODE_TO_CURRENCY,
  CurrencyName,
  CURRENCY_TO_SYMBOL as CURRENCY_NAME_TO_SYMBOL,
  EUROPEAN_COUNTRIES,
} from '../util/country';

export const EVM_CRYPTOCURRENCIES = [
  'AVAX',
  'ETH',
  'FTM',
  'BNB',
  'MATIC',
] as const;

export const SUPPORTED_CRYPTOCURRENCIES = [
  ...EVM_CRYPTOCURRENCIES,
  'SOL',
] as const;

export type EvmCryptocurrency = typeof EVM_CRYPTOCURRENCIES[number];
export type SupportedCryptocurrency = typeof SUPPORTED_CRYPTOCURRENCIES[number];

export type OrWrapped<T extends string> = T | `W${T}`;

export type PriceCrypto = {
  wei: number | string | bigint;
  cryptocurrency: SupportedCryptocurrency;
  wrapped?: boolean;
};

export type CurrencyOptions = {
  currency: { name: string; symbol: string };
  exchangeRates: { [key: string]: number };
};

export type CurrencyState = {
  currencyOptions: CurrencyOptions;
  exchangeRatesReady: boolean;
  format: (
    amount: number,
    symbol: string,
    options?: Intl.NumberFormatOptions,
  ) => string;
  formatFiat: (priceCrypto: PriceCrypto) => string;
  formatCrypto: (priceCrypto: PriceCrypto) => string;
  convertFiat: (priceCrypto: PriceCrypto) => number;
};

type CurrencyUpdate = {
  handleCountryChange: (countryCode: CountryCode) => void;
  exchangeRatesReady: boolean;
};

const CurrencyContext = createContext({}) as unknown as Context<CurrencyState>;
const CurrencyUpdateContext = createContext(
  {},
) as unknown as Context<CurrencyUpdate>;

export function useCurrency() {
  return useContext(CurrencyContext);
}

export function useCurrencyUpdate() {
  return useContext(CurrencyUpdateContext);
}

export function CurrencyProvider({ children }) {
  const [defaultExchangeRates, setDefaultExchangesRates] = useState({});
  // const [onramperExchangeRates, setOnramperExchangeRates] = useState({});
  const [exchangeRatesReady, setExchangeRatesReady] = useState(false);
  useEffect(() => {
    const handler = async () => {
      let rates = {};

      await Promise.all(
        SUPPORTED_CRYPTOCURRENCIES.map(async (coin) => {
          const response = await fetch(
            `https://onramper.tech/rate/USD/${coin}/creditCard/100?country=US`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Basic ${process.env.NEXT_PUBLIC_ONRAMPER_KEY}`,
              },
            },
          );
          const data = await response.json();
          if (data && data.length > 0) {
            const bestRate = data[0].rate;
            const objectToAdd = { [`${coin}`]: bestRate };
            rates = {
              ...rates,
              ...objectToAdd,
            };
          }
        }),
      );
      setDefaultExchangesRates(rates);
      setExchangeRatesReady(true);
      // setOnramperExchangeRates(rates);
    };
    handler();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [currencyOptions, setCurrencyOptions] = useState<CurrencyOptions>({
    currency: { name: 'USD', symbol: '$' },
    exchangeRates: {
      AVAX: 1 / 0.0364,
    },
  });

  const handleCountryChange = async (countryCode: CountryCode) => {
    localStorage.setItem('selectedCountry', countryCode);
    const isEuropean = EUROPEAN_COUNTRIES.includes(countryCode);
    const currency = isEuropean
      ? COUNTRY_CODE_TO_CURRENCY['EU']
      : COUNTRY_CODE_TO_CURRENCY[`${countryCode}`];
    //api call
    const country = isEuropean
      ? COUNTRY_CODE_TO_CURRENCY['EU'].name
      : COUNTRY_CODE_TO_CURRENCY[`${countryCode}`]?.name;
    const getRates = async () => {
      let rates = {};
      if (!currency || !country) {
        return rates;
      }
      await Promise.all(
        SUPPORTED_CRYPTOCURRENCIES.map(async (coin) => {
          const response = await fetch(
            `https://onramper.tech/rate/${country}/${coin}/creditCard/100?country=${countryCode}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Basic ${process.env.NEXT_PUBLIC_ONRAMPER_KEY}`,
              },
            },
          );
          const data = await response.json();
          if (data && data.length > 0) {
            const bestRate = data[0].rate;
            const objectToAdd = { [`${coin}`]: bestRate };
            rates = {
              ...rates,
              ...objectToAdd,
            };
          }
        }),
      );
      return rates;
    };
    const exchangeRates = await getRates();
    //{}
    if (
      Object.keys(exchangeRates).length === 0 ||
      Object.values(exchangeRates).some((rate) => {
        return !rate;
      })
    ) {
      setExchangeRatesReady(true);
      return setCurrencyOptions({
        currency: COUNTRY_CODE_TO_CURRENCY['US'],
        exchangeRates: defaultExchangeRates,
      });
    }
    //if exchange rates is an empty object
    //or one of the rates is undefined
    //default to US
    setExchangeRatesReady(true);
    setCurrencyOptions({
      currency,
      exchangeRates,
    });
  };

  const format = useCallback(
    (
      amount: number,
      currencyName: string,
      options: Intl.NumberFormatOptions = {},
    ) => {
      const isCurrency = CURRENCY_NAME_TO_SYMBOL.has(
        currencyName as CurrencyName,
      );
      return `${new Intl.NumberFormat(
        typeof window !== 'undefined' ? navigator?.language : undefined,
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          style: isCurrency ? 'currency' : undefined,
          currency: isCurrency ? currencyName : undefined,
          currencyDisplay: 'narrowSymbol',
          ...options,
        },
      ).format(amount)}${isCurrency ? '' : ` ${currencyName}`}`;
    },
    [],
  );

  const formatCrypto = ({
    wei,
    cryptocurrency,
    wrapped = false,
  }: PriceCrypto) => {
    return format(
      Number(wei) / 1e18,
      `${wrapped ? 'W' : ''}${cryptocurrency}`,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: undefined,
        minimumSignificantDigits: 4,
        maximumSignificantDigits: 4,
      },
    );
  };

  const convertFiat = ({ wei, cryptocurrency }: PriceCrypto) => {
    return (
      (Number(wei) / 1e18) * currencyOptions.exchangeRates[`${cryptocurrency}`]
    );
  };

  const formatFiat = (price: PriceCrypto) => {
    const fiat = convertFiat(price);
    if (!fiat && fiat !== 0) {
      return '';
    }
    return format(fiat, currencyOptions.currency.name);
  };

  return (
    <CurrencyContext.Provider
      value={{
        currencyOptions,
        exchangeRatesReady,
        format,
        formatFiat,
        formatCrypto,
        convertFiat,
      }}
    >
      <CurrencyUpdateContext.Provider
        value={{ handleCountryChange, exchangeRatesReady }}
      >
        {children}
      </CurrencyUpdateContext.Provider>
    </CurrencyContext.Provider>
  );
}
